"use client"

import { useMemo, useState } from "react"
import { Download } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useTimelineCsv } from "@/hooks/use-timeline-csv"
import { useGetCandidatesCandidateIdTimeline } from "openapi/api/endpoints"
import { asArray } from "@/lib/as-array"

interface TimelineViewerProps {
  candidateId: string
  candidateName?: string
  onClose: () => void
}

const FILTERS = [
  { value: "all", label: "All activity" },
  { value: "outreach_sent", label: "Outreach" },
  { value: "comment", label: "Comments" },
  { value: "task", label: "Tasks" },
  { value: "stage_transition", label: "Stage changes" },
  { value: "calendar_event", label: "Meetings" },
]

/**
 * The candidate's activity, rendered the way the job console renders a job's: a
 * three-column ledger of timestamp, primitive, record.
 *
 * The primitive — `outreach sent`, `stage moved` — is machine output and stays
 * mono even though it reads as words. That column is the receipt trail for the
 * candidate, which is the whole reason the ledger exists.
 *
 * This used to render as `fixed inset-0 z-50`: a full-screen layer covering the
 * entire app rather than the tab that opened it, chat pane included. It is an
 * in-flow panel now.
 */
export function TimelineViewer({ candidateId }: TimelineViewerProps) {
  const [filterType, setFilterType] = useState<string>("all")
  const { data } = useGetCandidatesCandidateIdTimeline(candidateId)

  const filteredItems = useMemo(() => {
    const items = asArray<NonNullable<typeof data>[number]>(data)
    const filtered =
      filterType === "all" ? items : items.filter((item) => item.type === filterType)
    return [...filtered].sort(
      (a, b) =>
        new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
    )
  }, [filterType, data])

  const { exportCsv, hasData } = useTimelineCsv(filteredItems, {
    filenameBase: "candidate_timeline",
    stripHtmlBodies: true,
    sortDescending: true,
  })

  return (
    <div className="rounded-[10px] border bg-card px-4 py-4 @2xl:px-6 @2xl:py-5">
      <div className="flex flex-wrap items-center gap-2 border-b pb-3.5">
        <span className="text-[11.5px] font-medium text-muted-foreground">
          Activity
        </span>
        <span className="font-mono text-[11px] text-muted-foreground">
          {filteredItems.length}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-8 w-[150px] text-xs">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              {FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>
                  {filter.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground"
            onClick={exportCsv}
            disabled={!hasData}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export
          </Button>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <p className="py-8 text-center text-xs text-muted-foreground">
          No activity recorded yet.
        </p>
      ) : (
        filteredItems.map((item) => (
          <div
            key={item.id}
            className="grid grid-cols-[minmax(0,1fr)] items-baseline gap-1 border-b py-2 @2xl:grid-cols-[96px_120px_minmax(0,1fr)] @2xl:gap-3.5"
          >
            <span className="font-mono text-[10.5px] leading-[1.5] text-muted-foreground">
              {formatWhen(item.occurred_at)}
            </span>
            {/* The primitive that ran — machine output, hence mono. */}
            <span className="font-mono text-[10.5px] leading-[1.5] text-brand-ink">
              {String(item.type).replace(/_/g, " ")}
            </span>
            <span className="min-w-0 text-xs leading-[1.5]">
              {item.title}
              {item.subtitle && (
                <span className="text-muted-foreground"> — {item.subtitle}</span>
              )}
            </span>
          </div>
        ))
      )}
    </div>
  )
}

/** Today shows a clock; anything older shows a date. */
function formatWhen(value: string) {
  const when = new Date(value)
  if (Number.isNaN(when.getTime())) return "—"
  const isToday = new Date().toDateString() === when.toDateString()
  return isToday
    ? when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : when.toLocaleDateString(undefined, { day: "numeric", month: "short" })
}
